/**
 * Leviathan - Quotes List Page
 * Quasar Prime LLC
 *
 * Sales quotes/proposals management
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  FileText,
  Search,
  Filter,
  Plus,
  Loader2,
  Eye,
  Send,
  Check,
  X,
  Copy,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
  DollarSign,
  Calendar,
  MoreVertical,
} from 'lucide-react';
import { API_URL } from '@/config/api';

const QUOTE_STATUSES = [
  { value: 'DRAFT', label: 'Draft', color: 'bg-slate-500/20 text-slate-400' },
  { value: 'SENT', label: 'Sent', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'VIEWED', label: 'Viewed', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'ACCEPTED', label: 'Accepted', color: 'bg-green-500/20 text-green-400' },
  { value: 'REJECTED', label: 'Rejected', color: 'bg-red-500/20 text-red-400' },
  { value: 'EXPIRED', label: 'Expired', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'CONVERTED', label: 'Converted', color: 'bg-amber-500/20 text-amber-400' },
];

interface Quote {
  id: string;
  quoteNumber: string;
  status: string;
  title: string | null;
  total: number;
  validUntil: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  createdAt: string;
  leadId: string | null;
  customerId: string | null;
  lead?: { id: string; leadNumber: string; companyName: string } | null;
  customer?: { id: string; customerNumber: string; name: string } | null;
  items: { id: string; description: string; quantity: number; lineTotal: number }[];
}

interface QuoteStats {
  total: number;
  draft: number;
  sent: number;
  accepted: number;
  rejected: number;
  totalValue: number;
  acceptedValue: number;
  conversionRate: number;
}

export default function QuotesList() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Fetch quotes
  const { data: quotesData, isLoading } = useQuery({
    queryKey: ['quotes', { search, status: statusFilter, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', page.toString());
      params.set('pageSize', '25');

      const res = await fetch(`${API_URL}/api/quotes?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch quotes');
      return res.json();
    },
  });

  // Fetch stats
  const { data: stats } = useQuery<QuoteStats>({
    queryKey: ['quotes-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  // Send quote mutation
  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to send quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-stats'] });
      setActiveDropdown(null);
    },
  });

  // Accept quote mutation
  const acceptMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to accept quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-stats'] });
      setActiveDropdown(null);
    },
  });

  // Reject quote mutation
  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to reject quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-stats'] });
      setActiveDropdown(null);
    },
  });

  // Duplicate quote mutation
  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to duplicate quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-stats'] });
      setActiveDropdown(null);
    },
  });

  const quotes = quotesData?.data || [];
  const totalPages = quotesData?.pagination?.totalPages || 1;
  const total = quotesData?.pagination?.total || 0;

  const getStatusBadge = (status: string) => {
    const statusInfo = QUOTE_STATUSES.find((s) => s.value === status);
    return statusInfo?.color || 'bg-slate-500/20 text-slate-400';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Quotes</h1>
          <p className="text-slate-400">Manage sales quotes and proposals</p>
        </div>
        <Link
          to="/sales/quotes/new"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Quote
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Quotes</p>
                <p className="text-xl font-bold text-slate-100">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20 text-green-400">
                <Check className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Accepted</p>
                <p className="text-xl font-bold text-slate-100">{stats.accepted}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Value</p>
                <p className="text-xl font-bold text-slate-100">{formatCurrency(stats.totalValue)}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Conversion Rate</p>
                <p className="text-xl font-bold text-slate-100">{stats.conversionRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search quotes..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            showFilters || statusFilter
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
              : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-slate-200'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {statusFilter && (
            <span className="w-5 h-5 rounded-full bg-amber-500 text-slate-900 text-xs flex items-center justify-center">
              1
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">All Statuses</option>
              {QUOTE_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
              className="px-4 py-2 text-slate-400 hover:text-slate-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-slate-400">
        Showing {quotes.length} of {total} quotes
      </div>

      {/* Quotes List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="bg-slate-800/50 rounded-xl p-12 text-center border border-slate-700/50">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No quotes found</h3>
          <p className="text-slate-500 mb-6">
            {search || statusFilter
              ? 'Try adjusting your filters'
              : 'Create your first quote to get started'}
          </p>
          <Link
            to="/sales/quotes/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Quote
          </Link>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Quote
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Customer/Lead
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Valid Until
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {quotes.map((quote: Quote) => (
                <tr key={quote.id} className="hover:bg-slate-700/20">
                  <td className="px-4 py-3">
                    <Link
                      to={`/sales/quotes/${quote.id}`}
                      className="text-amber-400 hover:text-amber-300 font-medium"
                    >
                      {quote.quoteNumber}
                    </Link>
                    {quote.title && (
                      <p className="text-sm text-slate-400 mt-0.5">{quote.title}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {quote.lead ? (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm text-slate-100">{quote.lead.companyName}</div>
                          <div className="text-xs text-slate-500">{quote.lead.leadNumber}</div>
                        </div>
                      </div>
                    ) : quote.customer ? (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm text-slate-100">{quote.customer.name}</div>
                          <div className="text-xs text-slate-500">{quote.customer.customerNumber}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(quote.status)}`}>
                      {quote.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-100 font-medium">
                    {formatCurrency(quote.total)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {formatDate(quote.validUntil)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === quote.id ? null : quote.id)}
                        className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {activeDropdown === quote.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 rounded-lg border border-slate-700 shadow-lg z-10">
                          <Link
                            to={`/sales/quotes/${quote.id}`}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Link>
                          {quote.status === 'DRAFT' && (
                            <button
                              onClick={() => sendMutation.mutate(quote.id)}
                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50"
                            >
                              <Send className="w-4 h-4" />
                              Send
                            </button>
                          )}
                          {(quote.status === 'SENT' || quote.status === 'VIEWED') && (
                            <>
                              <button
                                onClick={() => acceptMutation.mutate(quote.id)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-400 hover:bg-slate-700/50"
                              >
                                <Check className="w-4 h-4" />
                                Accept
                              </button>
                              <button
                                onClick={() => rejectMutation.mutate(quote.id)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-slate-700/50"
                              >
                                <X className="w-4 h-4" />
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => duplicateMutation.mutate(quote.id)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50"
                          >
                            <Copy className="w-4 h-4" />
                            Duplicate
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Click away to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </div>
  );
}
